import React from "react";

import { Link2, Link2Off, Plus, Settings } from "lucide-react";

import { Button } from "../../components/ui/button";
import { toast } from "../../components/ui/sonner";
import {
  selectsCrawlerPreferences,
  supportsMultipleAccounts,
} from "../constants";
import { useDelinkIntegration } from "../hooks/useDelinkIntegration";
import { useFetchConnectedIntegrationAccounts } from "../hooks/useFetchConnectedIntegration";

import { AccountCard } from "./shared/AccountCard";
import { ActionButton } from "./shared/ActionButton";
import { ErrorState } from "./shared/ErrorState";
import { LoadingState } from "./shared/LoadingState";
import { StepHeader } from "./shared/StepHeader";
import { StepLayout } from "./shared/StepLayout";

import type { ConnectedIntegrationDetail } from "../types/integrations";
import type { ModalStepProps } from "../types/modal";

export function ManageStep({
  integration,
  integrationScope,
  onClose,
  onConnect,
  onManage,
  isLoading,
}: ModalStepProps) {
  const {
    data: connectedAccounts = [],
    isLoading: isLoadingConnected,
    error: connectedError,
  } = useFetchConnectedIntegrationAccounts(integration.label, {
    integrationScope,
  });

  const { mutate: delinkIntegration, isPending: isDelinking } =
    useDelinkIntegration({
      onSuccess: () => {
        toast.success("Integration delinked successfully");
        onClose?.();
      },
    });

  const handleDelink = (account: ConnectedIntegrationDetail) => {
    delinkIntegration({
      verifiableId: account.verifiable_id,
      source: account.source,
      integrationScope,
    });
  };

  const renderAccountActions = (account: ConnectedIntegrationDetail) => (
    <>
      {selectsCrawlerPreferences(integration.label, integrationScope) &&
        account.is_valid_token && (
          <ActionButton
            variant="outline"
            size="sm"
            className="h-8 px-3 text-sm font-semibold border border-zinc-200 dark:border-neutral-600 bg-white dark:bg-neutral-700 hover:bg-zinc-50 dark:hover:bg-neutral-600 shadow-sm"
            onClick={() => onManage?.(account.verifiable_id)}
            disabled={isLoading || isDelinking}
            icon={<Settings className="w-4 h-4" />}
            data-testid="account-manage-button"
          >
            Manage
          </ActionButton>
        )}
      {!account.is_valid_token && (
        <ActionButton
          variant="outline"
          size="sm"
          className="h-8 px-3 text-sm font-semibold border border-zinc-200 dark:border-neutral-600 bg-white dark:bg-neutral-700 hover:bg-zinc-50 dark:hover:bg-neutral-600 shadow-sm"
          onClick={() => onConnect?.(account.verifiable_id)}
          disabled={isLoading || isDelinking}
          loading={isLoading}
          icon={<Link2 className="w-3 h-3" />}
          data-testid="account-reconnect-button"
        >
          Reconnect
        </ActionButton>
      )}
      <ActionButton
        variant="destructive"
        size="sm"
        className="h-8 px-3 text-sm font-semibold shadow-sm"
        onClick={() => handleDelink(account)}
        disabled={isLoading || isDelinking}
        loading={isDelinking}
        icon={<Link2Off className="w-3 h-3" />}
        data-testid="account-delink-button"
      >
        Delink
      </ActionButton>
    </>
  );

  return (
    <StepLayout
      header={
        <StepHeader
          integration={integration}
          title={`${integration.value} - Connected accounts`}
        />
      }
      footer={
        /* Add new account button for multi-account integrations OR Close button for others */
        supportsMultipleAccounts(integration.label) ? (
          <Button
            variant="outline"
            className="w-full h-10 border border-zinc-200 dark:border-neutral-600 bg-white dark:bg-neutral-700 hover:bg-zinc-50 dark:hover:bg-neutral-600"
            onClick={() => onConnect?.()}
            disabled={isLoading || isDelinking}
            data-testid="add-new-account-button"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add new account
          </Button>
        ) : (
          <Button
            className="w-full h-10"
            onClick={onClose}
            disabled={isLoading || isDelinking}
            data-testid="manage-step-close-button"
          >
            Close
          </Button>
        )
      }
    >
      <div className="space-y-4">
        {isLoadingConnected ? (
          <div className="bg-neutral-100 rounded-lg p-5">
            <LoadingState message="Loading connection details..." />
          </div>
        ) : connectedError || connectedAccounts.length === 0 ? (
          <ErrorState
            title={
              connectedError
                ? "Unable to load connection details"
                : "No connected accounts found"
            }
            variant="error"
          />
        ) : (
          connectedAccounts.map((account, index) => (
            <AccountCard
              key={`${account.verifiable_id}-${index}`}
              account={account}
              sourceId={integration.label}
            >
              {renderAccountActions(account)}
            </AccountCard>
          ))
        )}
      </div>
    </StepLayout>
  );
}
